'use strict';

const {
	h2, h3, a, div, p, section, span, img, ul, li, hr, header, button, i
} = require('iblokz-snabbdom-helpers');
const {arr} = require('iblokz-data');

const dayjs = require('dayjs');
require('dayjs/locale/bg');
const localizedFormat = require('dayjs/plugin/localizedFormat');
const utc = require('dayjs/plugin/utc');
dayjs.extend(localizedFormat);
dayjs.extend(utc);
const eventTime = require('../../util/event-time');

const md = require('../../util/md');
const _rl = require('../../util/resolve-localized');
const {resolveLocalized, DEFAULT_LOCALE} = _rl.default || _rl;
const WHEN_KEYS = ['upcoming', 'past'];


const LINK_ICONS = {
	facebook: 'facebook',
	instagram: 'instagram',
	soundcloud: 'soundcloud',
	mixcloud: 'mixcloud',
	github: 'github',
	bandcamp: 'bandcamp',
	youtube: 'youtube-play',
	website: 'globe',
	other: 'link'
};

const linkTypeFromUrl = (url = '') => {
	if (/soundcloud\.com/i.test(url)) return 'soundcloud';
	if (/mixcloud\.com/i.test(url)) return 'mixcloud';
	if (/github\.com/i.test(url)) return 'github';
	if (/instagram\.com/i.test(url)) return 'instagram';
	if (/facebook\.com|fb\.com/i.test(url)) return 'facebook';
	if (/youtube\.com|youtu\.be/i.test(url)) return 'youtube';
	if (/bandcamp\.com/i.test(url)) return 'bandcamp';
	return 'website';
};

const linkHostLabel = (url = '') => {
	try {
		return String(new URL(url).hostname || '').replace(/^www\./i, '') || url;
	} catch (err) {
		return String(url).replace(/^https?:\/\//i, '').split('/')[0] || url;
	}
};

const linkMeta = (link, i18n) => {
	const url = typeof link === 'string' ? link : (link && link.url) || '';
	const type = (link && link.type) || linkTypeFromUrl(url);
	const icon = LINK_ICONS[type] || LINK_ICONS.other;
	const labels = (i18n && i18n.enums && i18n.enums.type) || {};
	const label = (type === 'website' || type === 'other')
		? linkHostLabel(url)
		: (labels[type] || type);
	return {url, type, icon, label};
};

const pageCopy = (i18n, key, fallback) =>
	(i18n && i18n.pages && i18n.pages.calendar && i18n.pages.calendar[key]) || fallback;

const typeLabel = (i18n, type) => {
	const labels = (i18n && i18n.enums && i18n.enums.type) || {};
	return (type && labels[type]) || type || '';
};

const eventTitle = (event, lang, i18n) =>
	resolveLocalized(event && event.title, lang)
	|| pageCopy(i18n, 'untitled', 'Untitled event');

const eventTz = event => (event && event.timezone) || eventTime.DEFAULT_TZ;

const formatWhen = (event, lang) => {
	dayjs.locale(lang === 'en' ? 'en' : 'bg');
	const tz = eventTz(event);
	const start = eventTime.parse(event && event.startDateTime, tz);
	if (!start) return '';
	const end = eventTime.parse(event && event.endDateTime, tz);
	if (end && !end.isSame(start, 'day')) {
		return `${start.format('ll HH:mm')} – ${end.format('ll HH:mm')}`;
	}
	if (end) {
		return `${start.format('ll HH:mm')} – ${end.format('HH:mm')}`;
	}
	return start.format('ll HH:mm');
};

/** Date + time parts for the detail header (stacked under a calendar icon). */
const formatWhenParts = (event, lang) => {
	dayjs.locale(lang === 'en' ? 'en' : 'bg');
	const tz = eventTz(event);
	const start = eventTime.parse(event && event.startDateTime, tz);
	if (!start) return null;
	const end = eventTime.parse(event && event.endDateTime, tz);
	if (end && !end.isSame(start, 'day')) {
		return {
			date: `${start.format('ll')} – ${end.format('ll')}`,
			time: `${start.format('HH:mm')} – ${end.format('HH:mm')}`
		};
	}
	return {
		date: start.format('ll'),
		time: end
			? `${start.format('HH:mm')} – ${end.format('HH:mm')}`
			: start.format('HH:mm')
	};
};

const startMs = event => new Date(event.startDateTime || 0).getTime();
const sortDesc = (a, b) => startMs(b) - startMs(a);
const sortAsc = (a, b) => startMs(a) - startMs(b);

const isUpcoming = event => {
	const end = event.endDateTime || event.startDateTime;
	return new Date(end || 0).getTime() >= Date.now();
};

const eventPath = event => event.slug || event._id;

const readFilters = state => {
	const f = (state.events && state.events.filters) || {};
	return {
		// unset → both; explicit [] → none
		when: Array.isArray(f.when) ? f.when : WHEN_KEYS.slice(),
		// empty → all types
		types: Array.isArray(f.types) ? f.types : []
	};
};

const toggleFilter = (actions, key, value, fallback) => {
	actions.set(['events', 'filters', key], cur => {
		const list = Array.isArray(cur) ? cur : (fallback || []);
		return arr.toggle(list, value);
	});
};

const uniqueTypes = list => list.reduce((types, ev) => {
	if (ev.type && types.indexOf(ev.type) === -1) types.push(ev.type);
	return types;
}, []);

const matchesType = (event, types) =>
	!types.length || (event.type && types.indexOf(event.type) > -1);

const eventListItem = ({event, lang, i18n, actions}) => {
	const path = eventPath(event);
	return li([
		a(`[href="/calendar/${path}"]`, {
			on: {
				click: ev => {
					ev.preventDefault();
					actions.router.go(`calendar.${path}`);
				}
			}
		}, [
			event.poster
				? img('.poster', {attrs: {src: event.poster, alt: ''}})
				: span('.poster.placeholder'),
			span('.event-meta', [
				span('.name', eventTitle(event, lang, i18n)),
				span('.event-meta-row', [
					event.type
						? span('.tag.type', typeLabel(i18n, event.type))
						: '',
					span('.when', formatWhen(event, lang))
				])
			])
		])
	]);
};

const eventGroup = ({title, events, lang, i18n, actions}) =>
	events.length === 0
		? ''
		: div('.event-group', [
			h3('.event-group-title', title),
			ul('.event-list', events.map(event =>
				eventListItem({event, lang, i18n, actions})
			))
		]);

const toggleGroup = ({className, items, active, onToggle}) =>
	ul(`.toggle-group${className ? '.' + className : ''}`, items.map(({key, label}) =>
		li([
			button({
				attrs: {type: 'button'},
				class: {active: active.indexOf(key) > -1},
				on: {click: () => onToggle(key)}
			}, label)
		])
	));

const listView = ({state, actions, i18n}) => {
	const lang = state.lang || DEFAULT_LOCALE;
	const filters = readFilters(state);
	const list = (state.events.list || [])
		.filter(ev => !ev.status || ev.status === 'published');

	const types = uniqueTypes(list);
	const typed = list.filter(ev => matchesType(ev, filters.types));

	const showUpcoming = filters.when.indexOf('upcoming') > -1;
	const showPast = filters.when.indexOf('past') > -1;

	const upcoming = showUpcoming
		? typed.filter(isUpcoming).slice().sort(sortAsc)
		: [];
	const past = showPast
		? typed.filter(ev => !isUpcoming(ev)).slice().sort(sortDesc)
		: [];

	const empty = list.length === 0
		|| (upcoming.length === 0 && past.length === 0);

	const feedHost = (typeof location !== 'undefined' && location.host)
		? location.host
		: 'musictech.bg';

	return section('.events', [
		header('.events-bar', [
			h2('.events-title', pageCopy(i18n, 'title', 'Calendar')),
			div('.events-feeds', [
				a('.events-feed.rss', {
					attrs: {
						href: '/api/events.rss',
						title: pageCopy(i18n, 'rss', 'RSS feed'),
						target: '_blank',
						rel: 'noopener alternate',
						type: 'application/rss+xml'
					}
				}, [i('.fa.fa-rss')]),
				a('.events-feed.ics', {
					attrs: {
						href: `webcal://${feedHost}/api/events.ics`,
						title: pageCopy(i18n, 'ics', 'Subscribe in calendar (iCal)')
					}
				}, [i('.fa.fa-calendar')])
			]),
			div('.events-filters', [
				toggleGroup({
					className: 'when',
					items: [
						{key: 'upcoming', label: pageCopy(i18n, 'upcoming', 'Upcoming')},
						{key: 'past', label: pageCopy(i18n, 'past', 'Past')}
					],
					active: filters.when,
					onToggle: key => toggleFilter(actions, 'when', key, WHEN_KEYS.slice())
				}),
				types.length
					? toggleGroup({
						className: 'types',
						items: types.map(type => ({
							key: type,
							label: typeLabel(i18n, type)
						})),
						active: filters.types,
						onToggle: key => toggleFilter(actions, 'types', key, [])
					})
					: ''
			])
		]),
		div('.events-panel', [
			div('.events-scroll', [
				empty
					? p('.events-empty', pageCopy(i18n, 'empty', 'No published events yet.'))
					: div('.event-groups', [
						eventGroup({
							title: pageCopy(i18n, 'upcoming', 'Upcoming'),
							events: upcoming,
							lang,
							i18n,
							actions
						}),
						upcoming.length && past.length
							? hr('.event-split')
							: '',
						eventGroup({
							title: pageCopy(i18n, 'past', 'Past'),
							events: past,
							lang,
							i18n,
							actions
						})
					])
			])
		])
	]);
};

const detailView = ({state, actions, i18n}) => {
	const lang = state.lang || DEFAULT_LOCALE;
	const event = state.events.doc || {};
	const title = eventTitle(event, lang, i18n);
	const text = resolveLocalized(event.text, lang);
	const links = event.links || [];
	const whenParts = formatWhenParts(event, lang);
	const type = typeLabel(i18n, event.type);
	const icsHref = event.slug || event._id
		? `/api/events/${event.slug || event._id}.ics`
		: '';

	const goList = ev => {
		ev.preventDefault();
		actions.router.go('calendar');
	};

	return section('.events.event-detail', [
		div('.event-layout', [
			section('.event-block', [
				div('.event-block-header', [
					div('.event-block-heading', [
						h2('.event-block-name', [
							a('.event-name-path[href="/calendar"]', {
								on: {click: goList}
							}, pageCopy(i18n, 'title', 'Calendar')),
							span('.event-name-sep', ' / '),
							span('.event-name-current', title)
						])
					]),
					whenParts
						? div('.event-when', [
							span('.event-when-body', [
								span('.event-when-date', [
									i('.fa.fa-calendar'),
									span(whenParts.date)
								]),
								whenParts.time
									? span('.event-when-time', whenParts.time)
									: ''
							])
						])
						: ''
				]),
				div('.event-summary', [
					div('.event-summary-start', [
						type ? span('.tag.event-type', type) : ''
					]),
					links.length
						? ul('.event-links', links.map(link => {
							const {url, icon, label} = linkMeta(link, i18n);
							if (!url) return '';
							return li([
								a('.event-link[target="_blank"][rel="noopener"]', {
									attrs: {
										href: url,
										title: label
									}
								}, [
									span(`.event-link-icon.fa.fa-${icon}`),
									span('.event-link-label', label)
								])
							]);
						}))
						: div('.event-summary-mid'),
					icsHref
						? a('.event-ics', {
							attrs: {
								href: icsHref,
								download: `${event.slug || event._id}.ics`,
								title: pageCopy(i18n, 'addToCalendar', 'Add to calendar')
							}
						}, [
							i('.fa.fa-calendar-plus-o'),
							span(pageCopy(i18n, 'addToCalendar', 'Add to calendar'))
						])
						: div('.event-summary-end')
				]),
				div('.event-block-scroll', [
					div('.event-body', [
						text
							? div('.event-text', {
								props: {innerHTML: md.toDisplayHTML(text)}
							})
							: ''
					])
				])
			])
		])
	]);
};

module.exports = ({state, actions, i18n}) =>
	state.router.path[1]
		? detailView({state, actions, i18n})
		: listView({state, actions, i18n});
